import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

interface PersistenceCheckResult {
  isPersisted: boolean;
  wasRequested: boolean;
}

/**
 * Storage persistence adapter using Browser Storage API
 * Handles persistence requests and monitors app installation/visibility events
 * Framework-agnostic implementation (uses RxJS for event handling)
 */
export class StorageService {
  private static readonly CHECK_COOLDOWN_MS = 60000; // 1 minute
  private static readonly RECHECK_DELAY_MS = 1000; // Delay after installation/mode change

  private persistedInThisSession = false;
  private checkInProgress = false;
  private lastCheckTime = 0;

  private visibilitySubscription?: Subscription;
  private appInstalledListener?: (() => void) | undefined;
  private displayModeListener?: ((e: MediaQueryListEvent) => void) | undefined;
  private displayModeQuery?: MediaQueryList | undefined;

  /**
   * Initialize storage persistence checking.
   * Should be called once during app initialization.
   */
  async initialize(): Promise<void> {
    await this.ensureStoragePersistence();

    // Skip setting up listeners if already persisted
    if (this.persistedInThisSession) {
      return;
    }

    this.setupEventListeners();
  }

  // todo [ds]: ensureStoragePersistence is obsolete
  /**
   * Check current persistence status and request if needed.
   * @param force - If true, bypass cooldown check and force the persistence check
   */
  async ensureStoragePersistence(force = false): Promise<PersistenceCheckResult> {
    // Early return if already confirmed in this session
    if (this.persistedInThisSession) {
      return { isPersisted: true, wasRequested: false };
    }

    // Respect cooldown to avoid excessive checks (unless forced)
    if (!force && (this.checkInProgress || !this.isCooldownElapsed())) {
      return { isPersisted: false, wasRequested: false };
    }

    return await this.performCheck();
  }

  /**
   * Check if storage is currently persisted (cached result if available).
   */
  async isPersisted(): Promise<boolean> {
    if (this.persistedInThisSession) {
      return true;
    }

    if (!navigator.storage?.persisted) {
      return false;
    }

    try {
      return await navigator.storage.persisted();
    } catch (error) {
      console.error('[StoragePersistence] Error checking persistence:', error);
      return false;
    }
  }

  private async performCheck(): Promise<PersistenceCheckResult> {
    this.checkInProgress = true;
    this.lastCheckTime = Date.now();

    try {
      if (!navigator.storage?.persist) {
        this.log('Browser does not support persistent storage', 'warn');
        return { isPersisted: false, wasRequested: false };
      }

      const alreadyPersisted = await navigator.storage.persisted();

      if (alreadyPersisted) {
        this.log('Storage is already persistent');
        this.markAsPersistedAndCleanup();
        return { isPersisted: true, wasRequested: false };
      }

      // Request persistence
      this.log('Requesting persistent storage...');
      const granted = await navigator.storage.persist();

      if (granted) {
        this.log('✓ Persistent storage granted');
        this.markAsPersistedAndCleanup();
      } else {
        this.log('✗ Persistent storage denied', 'warn');
      }

      return { isPersisted: granted, wasRequested: true };
    } catch (error) {
      console.error('[StoragePersistence] Error during persistence check:', error);
      return { isPersisted: false, wasRequested: false };
    } finally {
      this.checkInProgress = false;
    }
  }

  private isCooldownElapsed(): boolean {
    return Date.now() - this.lastCheckTime > StorageService.CHECK_COOLDOWN_MS;
  }

  private markAsPersistedAndCleanup(): void {
    this.persistedInThisSession = true;
    this.cleanupEventListeners();
  }

  private setupEventListeners(): void {
    this.setupInstallationListeners();
    this.setupVisibilityListener();
  }

  private setupInstallationListeners(): void {
    // Listen for app installation
    if ('onappinstalled' in window) {
      this.appInstalledListener = () => {
        this.log('App installed, checking persistence...');
        setTimeout(() => this.ensureStoragePersistence(), StorageService.RECHECK_DELAY_MS);
      };
      window.addEventListener('appinstalled', this.appInstalledListener);
    }

    // Monitor display mode changes (standalone = installed)
    if (window.matchMedia) {
      this.displayModeQuery = window.matchMedia('(display-mode: standalone)');
      this.displayModeListener = (e) => {
        if (e.matches) {
          this.log('App running in standalone mode, checking persistence...');
          setTimeout(() => this.ensureStoragePersistence(), StorageService.RECHECK_DELAY_MS);
        }
      };
      this.displayModeQuery.addEventListener('change', this.displayModeListener);
    }
  }

  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      debounceTime(1000),
    );

    const focus$ = fromEvent(window, 'focus').pipe(debounceTime(1000));

    this.visibilitySubscription = merge(visibility$, focus$).subscribe(() => {
      this.ensureStoragePersistence();
    });
  }

  private cleanupEventListeners(): void {
    this.visibilitySubscription?.unsubscribe();

    if (this.appInstalledListener) {
      window.removeEventListener('appinstalled', this.appInstalledListener);
      this.appInstalledListener = undefined;
    }

    if (this.displayModeListener && this.displayModeQuery) {
      this.displayModeQuery.removeEventListener('change', this.displayModeListener);
      this.displayModeListener = undefined;
      this.displayModeQuery = undefined;
    }

    this.log('Event listeners cleaned up');
  }

  private log(message: string, level: 'log' | 'warn' = 'log'): void {
    console[level](`[StoragePersistence] ${message}`);
  }
}

/**
 * Singleton instance of StorageService
 * Ensures single point of storage persistence management
 */
const storageServiceInstance = new StorageService();

/**
 * Initialize storage persistence checking
 * Should be called once during app initialization
 */
export async function initializeStoragePersistence(): Promise<void> {
  return storageServiceInstance.initialize();
}

/**
 * Check and request storage persistence
 * @param force - If true, bypass cooldown and force check
 * @returns Status of persistence and whether request was made
 */
export async function ensureStoragePersistence(force = false): Promise<{
  isPersisted: boolean;
  wasRequested: boolean;
}> {
  return storageServiceInstance.ensureStoragePersistence(force);
}
